/* PrimaryNotificationsBell — bell for the primary client in the public
   interactive-gallery viewer (Phase G6). Polls /api/galleries/{slug}/notifications.
   Only mounted by Gallery/index.jsx when `role === 'primary'` on interactive
   galleries; collaborators and standard galleries don't see this.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Bell, X, Heart, UserPlus, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000;

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const KIND_ICON = {
  collaborator_joined: UserPlus,
  collaborator_favorited: Heart,
};

export default function PrimaryNotificationsBell({ slug, token, accent = "#C9A961", onJumpToPhoto }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 24 });
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const reposition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setPos({ top: r.bottom + 10, right: Math.max(12, window.innerWidth - r.right) });
  }, []);

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/galleries/${slug}/notifications/unread-count`, { headers });
      setUnread(Number(data?.count) || 0);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/galleries/${slug}/notifications`, { headers });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_MS);
    return () => clearInterval(id);
  }, [fetchUnread]);

  useEffect(() => {
    if (!open) return;
    reposition();
    fetchList();
    let raf = 0;
    const schedule = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; reposition(); }); };
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { passive: true });
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [open, reposition, fetchList]);

  useEffect(() => {
    function onClick(e) {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const onRowClick = useCallback(async (n) => {
    try {
      await axios.post(`${API}/galleries/${slug}/notifications/mark-read`, { ids: [n.id] }, { headers });
    } catch {}
    setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, read_at: new Date().toISOString() } : it)));
    setUnread((c) => Math.max(0, c - (n.read_at ? 0 : 1)));
    setOpen(false);
    if (n?.payload?.photo_id && typeof onJumpToPhoto === "function") {
      onJumpToPhoto(n.payload.photo_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, onJumpToPhoto]);

  const markAllRead = useCallback(async () => {
    try {
      await axios.post(`${API}/galleries/${slug}/notifications/mark-read`, { all: true }, { headers });
      setItems((prev) => prev.map((it) => ({ ...it, read_at: it.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="menu"
        className="relative inline-flex items-center justify-center w-8 h-8 text-stone-700 hover:text-stone-900 transition-colors"
        data-testid="primary-notifications-btn"
        title="Notifications"
      >
        <Bell className="w-4 h-4" strokeWidth={1.5} />
        {unread > 0 && (
          <span
            data-testid="primary-notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center text-[9.5px] font-medium rounded-full"
            style={{ backgroundColor: accent, color: "#0E0D0C" }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          role="menu"
          data-testid="primary-notifications-panel"
          style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 1000, width: 340 }}
          className="bg-[#0E0D0C] text-[#F5EFE0] border border-[#C9A961]/30 shadow-[0_24px_80px_-15px_rgba(0,0,0,0.6)]"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#C9A961]/20">
            <span className="text-[10px] uppercase tracking-[0.32em]" style={{ color: accent }}>
              Notifications{unread > 0 ? ` · ${unread}` : ""}
            </span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="w-6 h-6 inline-flex items-center justify-center rounded-full border border-[#C9A961]/30 text-[#F5EFE0]/70 hover:text-[#F5EFE0] hover:border-[#C9A961] transition-colors"
              data-testid="primary-notifications-close"
            >
              <X className="w-3 h-3" />
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto" data-testid="primary-notifications-list">
            {loading && (
              <div className="px-6 py-6 text-center text-[11px] uppercase tracking-[0.28em] text-[#F5EFE0]/40">Loading…</div>
            )}

            {!loading && items.length === 0 && (
              <div className="px-6 py-8 text-center">
                <div className="mx-auto w-10 h-10 inline-flex items-center justify-center mb-3" style={{ color: accent }}>
                  <Sparkles className="w-5 h-5" strokeWidth={1.25} />
                </div>
                <div className="font-serif italic text-xl text-[#F5EFE0]/90" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  Quiet so far.
                </div>
                <p className="mt-3 text-[11px] font-serif italic text-[#F5EFE0]/55 leading-relaxed max-w-[240px] mx-auto" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  You'll see who joins and what they love here.
                </p>
              </div>
            )}

            {!loading && items.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              const isUnread = !n.read_at;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onRowClick(n)}
                  data-testid={`primary-notifications-row-${n.id}`}
                  className={`w-full text-left flex gap-3 px-5 py-3 border-b border-[#C9A961]/15 hover:bg-white/[0.04] transition-colors ${isUnread ? "" : "opacity-55"}`}
                >
                  <span
                    className="shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-full border"
                    style={{ borderColor: `${accent}55`, color: accent }}
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[12.5px] leading-snug text-[#F5EFE0]/95">{n.title}</span>
                    <span className="block text-[11.5px] mt-0.5 text-[#F5EFE0]/55 truncate">{n.body}</span>
                    <span className="block text-[10px] mt-1 uppercase tracking-[0.22em] text-[#F5EFE0]/35">{relativeTime(n.created_at)}</span>
                  </span>
                  {isUnread && (
                    <span className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accent }} aria-hidden />
                  )}
                </button>
              );
            })}
          </div>

          {!loading && items.length > 0 && (
            <div className="flex items-center justify-end px-5 py-3 border-t border-[#C9A961]/20">
              <button
                onClick={markAllRead}
                disabled={unread === 0}
                data-testid="primary-notifications-mark-all"
                className="text-[10px] uppercase tracking-[0.28em] hover:text-[#F5EFE0] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                style={{ color: accent }}
              >
                Mark all read
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
